import { create } from 'zustand';
import { readLocal, writeLocal } from '../lib/storage';

export type ThemePreference = 'system' | 'light' | 'dark';

const KEY = 'doriavboard.theme';
const media = window.matchMedia('(prefers-color-scheme: dark)');

function isDark(preference: ThemePreference): boolean {
  return preference === 'dark' || (preference === 'system' && media.matches);
}

interface ThemeState {
  preference: ThemePreference;
  /**
   * Whether this person sees the app in dark mode right now. Personal: boards
   * are drawn dark for them while everyone else sees their own choice.
   */
  dark: boolean;
  setPreference(preference: ThemePreference): void;
}

function apply(preference: ThemePreference) {
  const dark = isDark(preference);
  document.documentElement.classList.toggle('dark', dark);
  useTheme.setState({ dark });
}

export const useTheme = create<ThemeState>((set) => {
  const preference = readLocal<ThemePreference>(KEY, 'system');
  return {
    preference,
    dark: isDark(preference),
    setPreference(next) {
      writeLocal(KEY, next);
      set({ preference: next });
      apply(next);
    },
  };
});

/** Switches between light and dark directly (from the board's top bar). */
export function toggleDarkMode(): void {
  useTheme.getState().setPreference(useTheme.getState().dark ? 'light' : 'dark');
}

/** Applies the saved theme now and follows the OS setting while on "system". */
export function initTheme(): void {
  apply(useTheme.getState().preference);
  media.addEventListener('change', () => apply(useTheme.getState().preference));
}
